import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Trash2 } from 'lucide-react';
import { rupees, setQty, useCart } from './shopApi';
import { AwaitingPayment } from './AwaitingPayment';
import { ProductPhoto, Stepper } from './ShopHome';
import { useShopFront } from './ShopLayout';

export function useTotals() {
  const front = useShopFront().data;
  const cart = useCart();
  const lines = (front?.products ?? []).filter((p) => cart[p.id]).map((p) => ({ product: p, qty: cart[p.id], total: Number(p.price) * cart[p.id] }));
  const subtotal = lines.reduce((a, l) => a + l.total, 0);
  const freeAbove = Number(front?.free_delivery_above ?? 0);
  const fee = lines.length && !(freeAbove && subtotal >= freeAbove) ? Number(front?.delivery_fee ?? 0) : 0;
  const short = Math.max(0, Number(front?.min_order ?? 0) - subtotal);
  // Items in the cart that the shop no longer sells (removed or hidden by the farm).
  const gone = Object.keys(cart).filter((id) => !(front?.products ?? []).some((p) => p.id === id));
  return { front, lines, subtotal, fee, total: subtotal + fee, short, gone, freeAbove };
}

export default function CartPage() {
  const navigate = useNavigate();
  const { front, lines, subtotal, fee, total, short, gone, freeAbove } = useTotals();
  const goneKey = gone.join(',');
  React.useEffect(() => { if (front) gone.forEach((id) => setQty(id, 0)); }, [goneKey, front]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!front) return null;
  const outOfStock = lines.some((l) => !l.product.in_stock);

  if (lines.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <AwaitingPayment />
        <div className="flex flex-col items-center gap-3 rounded-xl bg-white p-10 text-center">
          <ShoppingBag className="h-10 w-10 text-brand-green-600" />
          <p className="font-semibold">Your cart is empty</p>
          <p className="text-xs text-ink-500">Items move into your order once you place it.</p>
          <Link to="/shop" className="rounded-full bg-brand-green-700 px-5 py-2 text-sm font-semibold text-white">Browse products</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-xl font-bold text-brand-green-900">Your cart</h1>
      {lines.map(({ product: p, qty, total: lt }) => (
        <div key={p.id} className="flex gap-3 rounded-xl border border-brand-green-100 bg-white p-3">
          <ProductPhoto product={p} className="h-20 w-20 shrink-0 rounded-lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold">{p.name}</p>
                <p className="text-xs text-ink-400">{p.pack_label} · {rupees(p.price)} each</p>
              </div>
              <button type="button" aria-label="Remove" onClick={() => setQty(p.id, 0)} className="text-ink-400 hover:text-critical-600"><Trash2 className="h-4 w-4" /></button>
            </div>
            {!p.in_stock && <p className="text-xs font-semibold text-critical-600">Out of stock today — please remove it</p>}
            <div className="mt-auto flex items-center justify-between gap-3">
              <div className="w-28"><Stepper product={p} qty={qty} /></div>
              <p className="font-bold tabular-nums">{rupees(lt)}</p>
            </div>
          </div>
        </div>
      ))}

      <div className="rounded-xl bg-white p-4 text-sm">
        <div className="flex justify-between py-1"><span>Items</span><span className="tabular-nums">{rupees(subtotal)}</span></div>
        <div className="flex justify-between py-1"><span>Delivery</span><span className="tabular-nums">{fee ? rupees(fee) : 'Free'}</span></div>
        {freeAbove > 0 && fee > 0 && <p className="text-xs text-ink-400">Add {rupees(freeAbove - subtotal)} more for free delivery.</p>}
        <div className="mt-1 flex justify-between border-t border-dashed border-ink-300/50 pt-2 text-base font-bold"><span>Total</span><span className="tabular-nums text-brand-green-800">{rupees(total)}</span></div>
      </div>
      {short > 0 && <p className="rounded-lg bg-warning-50 p-3 text-sm text-warning-700">The smallest order is {rupees(front.min_order)} — add {rupees(short)} more.</p>}
      <button type="button" disabled={!front.open || short > 0 || outOfStock} onClick={() => navigate('/shop/checkout')}
        className="rounded-xl bg-brand-green-700 py-3.5 text-base font-bold text-white disabled:bg-ink-300">
        {front.open ? 'Continue to delivery details' : 'Ordering is paused'}
      </button>
      {!front.open && <p className="text-center text-sm text-ink-500">{front.paused_message}</p>}
    </div>
  );
}
